/** Delete draft (recoverable) / Cancel generated DC (void with reason, history kept). Hidden after dispatch. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Ban, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const err = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Failed"; };

export default function DcLifecycleActions({ dc, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const draft = ["DRAFT", "ADDRESS_VERIFIED"].includes(dc.status);
  const final = ["DC_READY", "READY_FOR_DISPATCH"].includes(dc.status) && dc.code;
  if (!draft && !final) return null;
  const go = async () => {
    if (final && reason.trim().length < 5) return toast.error("Enter a reason (min 5 characters)");
    setBusy(true);
    try {
      await api.delete(`/delivery-challans/${dc.id}`, { data: { reason: reason.trim() || null } });
      toast.success(final ? `${dc.code} cancelled — number and history kept, quantities and serials released` : "Draft deleted — Management can restore it from Deleted drafts");
      setOpen(false); onDone?.(final ? "cancelled" : "deleted");
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button type="button" size="sm" variant="ghost" className="h-8 text-[12px] text-rose-700 hover:bg-rose-50 hover:text-rose-800" onClick={() => { setReason(""); setOpen(true); }} data-testid={final ? "dc-void" : "dc-delete-draft"}>
        {final ? <><Ban className="h-3.5 w-3.5 mr-1" />Cancel DC</> : <><Trash2 className="h-3.5 w-3.5 mr-1" />Delete draft</>}
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-w-md" data-testid="dc-lifecycle-dialog">
          <DialogHeader>
            <DialogTitle>{final ? <>Cancel DC <span className="font-mono">{dc.code}</span>?</> : "Delete this draft DC?"}</DialogTitle>
            <DialogDescription>{final ? "The DC is voided, not erased: its number, PDF history and audit trail stay. Its quantities return to the order balance and reserved serials are released. Management only." : "The draft leaves the list and its reserved serials are released. A full copy is kept under Deleted drafts, where Management can restore it."}</DialogDescription>
          </DialogHeader>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={final ? "Reason (required)" : "Reason (optional)"} className="h-9 text-[12px]" data-testid="dc-lifecycle-reason" />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy} data-testid="dc-lifecycle-keep">Keep</Button>
            <Button type="button" className="bg-rose-600 hover:bg-rose-700" onClick={go} disabled={busy || (final && reason.trim().length < 5)} data-testid="dc-lifecycle-confirm">{final ? "Cancel DC" : "Delete draft"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function DeletedDcList({ orderId, onRestored, refreshKey }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/delivery-challans/archive/deleted", { params: { order_id: orderId || undefined } }).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [orderId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items?.length) return null;
  const restore = async (a) => {
    setBusy(a.archive_id);
    try {
      const { data } = await api.post(`/delivery-challans/archive/${a.archive_id}/restore`, {});
      toast.success(data.serials_left_off?.length ? `Draft restored — serials now used elsewhere were left off: ${data.serials_left_off.join(", ")}` : "Draft restored");
      load(); onRestored?.(data);
    } catch (e) { toast.error(err(e)); } finally { setBusy(""); }
  };
  return (
    <details className="rounded-md border border-slate-200 bg-white p-2 text-[12px]" data-testid="dc-deleted-list">
      <summary className="cursor-pointer text-slate-600">Deleted drafts ({items.length})</summary>
      <div className="mt-2 space-y-1.5">{items.map((a) => (
        <div key={a.archive_id} className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-1.5" data-testid={`dc-deleted-${a.archive_id}`}>
          <span className="font-mono text-[11px]">{a.order_code} · part {a.part_no}</span>
          <span className="text-slate-500">{(a.item_summary || []).join("; ")}</span>
          <span className="text-[11px] text-slate-500">deleted {String(a.deleted_at).slice(0, 16).replace("T", " ")} by {a.deleted_by}{a.delete_reason ? ` · ${a.delete_reason}` : ""}</span>
          <Button type="button" size="sm" variant="outline" className="ml-auto h-7 text-[11px]" disabled={!!busy} onClick={() => restore(a)} data-testid={`dc-restore-${a.archive_id}`}>Restore</Button>
        </div>))}</div>
    </details>
  );
}
